// UserPanelContext.tsx - Versión refactorizada

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../constants/config";
import { useSocket } from "./SocketContext";
import type { SystemHealth } from "../pages/UserPanel/types";

// Importar las stores
import { useUserStore } from "../shared/stores/useUserStore";
import { usePanelsStore } from "../shared/stores/usePanelsStore";
import { useRequestsStore } from "../shared/stores/useRequestsStore";
import { useRequestDetailsStore } from "../shared/stores/useRequestDetailsStore";
import { sanitizeAndStore, getAndSanitize } from '../shared/utils/sanitization';

interface SoundSettings {
  loadRequest: number;
  loadApproved: number;
  loadRejected: number;
  requiresValidation: number;
  withdrawRequest: number;
  userCreated: number;
}

interface SoundConfig {
  path: string;
  volume?: number;
}

// Este contexto ahora solo mantiene funcionalidades que no están en stores
interface UserPanelContextType {
  currentPanelId: string | null;
  soundEnabled: boolean;
  toggleSound: () => void;
  createUser: (data: any) => void;
  deposit: (formData: FormData) => Promise<any>;
  withdraw: (formData: FormData) => Promise<any>;
  isDepositing: boolean;
  isWithdrawing: boolean;
  checkBalance: (data: { username: string; panelId?: string }) => void;
  checkHistory: (data: { username: string; panelId?: string }) => Promise<any>;
  pendingRequests: any[];
  processedRequests: any[];
  processRequest: (data: any) => Promise<any>;
  hasNewRequests: boolean;
  setHasNewRequests: (value: boolean) => void;
  rejectRequest: (data: FormData) => Promise<any>;
  getSettings: () => Promise<any>;
  updateSettings: (values: any) => Promise<any>;
  panelLoginStatus: boolean;
  health: SystemHealth;
  socket?: any;
  updateCurrentPanelId: (panelId: string | null) => void;
}

// Valores por defecto reducidos
const UserPanelContext = createContext<UserPanelContextType>({
  currentPanelId: null,
  soundEnabled: false,
  toggleSound: () => {},
  createUser: () => {},
  deposit: async () => ({ data: null }),
  withdraw: async () => ({ data: null }),
  isDepositing: false,
  isWithdrawing: false,
  checkBalance: async () => {},
  checkHistory: async () => ({ data: null }),
  pendingRequests: [],
  processedRequests: [],
  processRequest: async () => ({ data: null }),
  hasNewRequests: false,
  setHasNewRequests: () => {},
  rejectRequest: async () => ({ data: null }),
  getSettings: async () => ({ data: null }),
  updateSettings: async () => ({ data: null }),
  panelLoginStatus: false,
  health: {
    status: "error" as const,
    ping: 0,
    memory: {
      heapTotal: 0,
      heapUsed: 0,
      external: 0,
      arrayBuffers: 0,
      rss: 0,
    },
    cpu: {
      user: 0,
      system: 0,
    },
    timestamp: Date.now(),
  },
  socket: null,
  updateCurrentPanelId: () => {}
});

export const UserPanelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const processedRequestIds = useRef<Set<string>>(new Set());

  const [soundEnabled, setSoundEnabled] = useState(
    () => getAndSanitize("soundEnabled") !== "false"
  );
  const queryClient = useQueryClient();
  const token = getAndSanitize("token");
  const [currentPanelId, setCurrentPanelId] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [processedRequests, setProcessedRequests] = useState<any[]>([]);
  const [hasNewRequests, setHasNewRequests] = useState(false);
  const [panelLoginStatus, setPanelLoginStatus] = useState(false);
  const { socket, isConnected, health, lastReconnectTimestamp, updatePanelId } = useSocket();

  // Acceder a las stores
  const panelsStore = usePanelsStore();
  const requestsStore = useRequestsStore();
  
  const headers = {
    Authorization: `Bearer ${getAndSanitize("token")}`,
  };

  // ELIMINADO: fetchRequests, requestsState, getUserPanelsImpl - ahora viene de los stores

  // Limpiar mensajes procesados periódicamente
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const idsToDelete: string[] = [];

      processedRequestIds.current.forEach((id) => {
        const [_, timestamp] = id.split("|");
        if (now - parseInt(timestamp) > 10000) {
          idsToDelete.push(id);
        }
      });

      idsToDelete.forEach((id) => processedRequestIds.current.delete(id));

      if (idsToDelete.length > 0) {
      }
    }, 30000);

    return () => clearInterval(cleanupInterval);
  }, []);

  const checkHistory = useMutation({
    mutationFn: async (data: { username: string; panelId?: string }) => {
      const adminId = getAndSanitize("userId");

      try {
        const panelId = data.panelId || currentPanelId;
        if (!panelId) {
          console.error("[History] No panel ID available");
          throw new Error("Se requiere seleccionar un panel");
        }


        const response = await axios.post<{ history: any[] }>(
          `${API_BASE_URL}/api/panel/operations/history`,
          {
            username: data.username,
            adminId,
            panelId,
          },
          {
            headers: {
              ...headers,
              "X-Admin-ID": adminId,
            },
          }
        );

        return response.data;
      } catch (error: any) {
        console.error("[History] Request failed:", error);
        throw error;
      }
    },
    onSuccess: (response, variables) => {

      queryClient.invalidateQueries({ queryKey: ["panel-users"] });
      
      queryClient.setQueryData(["panel-users"], (oldData: any[] = []) => {
        if (!oldData) return [];

        return oldData.map((user) => {
          if (user.username === variables.username) {
            return { ...user, historyData: response.history };
          }
          return user;
        });
      });
    },
  });

  // Create an audio cache to preload and reuse audio objects
  const audioCache = new Map<string, HTMLAudioElement>();

  // Sound configuration
  const SOUND_CONFIG: Record<keyof SoundSettings, SoundConfig> = {
    loadRequest: { path: "/sounds/load-request.mp3", volume: 1.0 },
    loadApproved: { path: "/sounds/load-approved.mp3", volume: 1.0 },
    loadRejected: { path: "/sounds/load-rejected.mp3", volume: 1.0 },
    requiresValidation: {
      path: "/sounds/requires-validation.mp3",
      volume: 1.0,
    },
    withdrawRequest: { path: "/sounds/withdraw-request.mp3", volume: 1.0 },
    userCreated: { path: "/sounds/createuser.mp3", volume: 1.0 },
  };

  // Preload sounds when component initializes
  useEffect(() => {
    if (!soundEnabled) return;


    Object.entries(SOUND_CONFIG).forEach(([type, config]) => {
      try {
        const absolutePath = new URL(config.path, window.location.origin).href;

        const audio = new Audio();
        audio.src = absolutePath;
        audio.load();

        audioCache.set(type, audio);
      } catch (err) {
        console.warn(`[Audio] Failed to preload ${type}:`, err);
      }
    });
  }, [soundEnabled]);

  // Obtener la ruta correcta del archivo de sonido
  const getSoundPath = (type: keyof SoundSettings): string => {
    const basePath = "/sounds";
    const soundFiles: Record<keyof SoundSettings, string> = {
      loadRequest: `${basePath}/load-request.mp3`,
      loadApproved: `${basePath}/load-approved.mp3`,
      loadRejected: `${basePath}/load-rejected.mp3`,
      requiresValidation: `${basePath}/requires-validation.mp3`,
      withdrawRequest: `${basePath}/withdraw-request.mp3`,
      userCreated: `${basePath}/createuser.mp3`,
    };
    return soundFiles[type];
  };

  // Reproducir sonido para solicitudes según su tipo/estado
  const playSoundForRequest = (request: any): void => {
    if (request.type === "WITHDRAW") {
      playSound("withdrawRequest");
      return;
    }

    switch (request.status) {
      case "PENDING":
        playSound("loadRequest");
        break;
      case "REQUIRES_VALIDATION":
        playSound("requiresValidation");
        break;
      case "COMPLETED":
        playSound("loadApproved");
        break;
      case "REJECTED":
        playSound("loadRejected");
        break;
      case "USER_CREATED":
        playSound("userCreated");
        break;
      default:
        playSound("loadRequest");
        break;
    }
  };

  // Reproducir sonido con volumen configurable
  const playSound = (soundType: keyof SoundSettings): void => {
    if (!soundEnabled) {
      return;
    }

    try {
      const savedVolumes = getAndSanitize('soundVolumes');
      const volumeSettings = savedVolumes ? JSON.parse(savedVolumes) : {
        loadRequest: 100,
        loadApproved: 100,
        loadRejected: 100,
        requiresValidation: 100,
        withdrawRequest: 100,
        userCreated: 100
      };
      
      const volumeLevel = volumeSettings[soundType] / 100;
      const audio = new Audio(getSoundPath(soundType));
      audio.volume = volumeLevel;
      
      audio.play();
    } catch (err) {
      console.warn(`[Sound] Error playing ${soundType}:`, err);
    }
  };

  // Función para normalizar las solicitudes recibidas por sockets
  const normalizeRequest = (request: any): any => {
    if (!request) return null;

    // Verificar si estamos recibiendo un objeto con la estructura esperada
    const hasBasicFields = request.id && (request.status !== undefined);
    
    if (!hasBasicFields) {
      console.warn("[Panel] Socket sent incomplete request:", request);
      return null;
    }

    // Asegurarnos que tiene todos los campos necesarios
    const normalizedRequest = {
      ...request,
      // Establecer valores por defecto para campos potencialmente ausentes
      type: request.type || (request.withdrawAmount ? "WITHDRAW" : "LOAD"),
      requestData: request.requestData || {},
      linkedTransfer: request.linkedTransfer || null,
      operation: request.operation || null,
      user: request.user || null,
    };

    // Si es un objeto processingRequest del backend que contiene sólo la estructura parcial
    if (typeof request.requestData === 'string') {
      try {
        normalizedRequest.requestData = JSON.parse(request.requestData);
      } catch (e) {
        console.warn("[Panel] Failed to parse requestData JSON:", e);
      }
    }

    return normalizedRequest;
  };

  // Actualizar la UI con el estado de la solicitud
  const handleRequestStateUpdate = (request: any) => {
    // Normalizar la solicitud para garantizar la estructura esperada
    const normalizedRequest = normalizeRequest(request);
    
    if (!normalizedRequest) {
      console.error("[Panel] Invalid request data received:", request);
      return;
    }


    // Invalidar cache de detalles para que se recargue con datos frescos
    useRequestDetailsStore.getState().invalidateCache(normalizedRequest.id);

    // Actualizar directamente el store en lugar del estado local
    requestsStore.handleRequestUpdate(normalizedRequest);

    // Actualizar notificaciones para solicitudes pendientes
    if (["PENDING", "REQUIRES_VALIDATION"].includes(normalizedRequest.status)) {
      setHasNewRequests(true);
    }
  };

  // Mutation para procesar solicitudes
  const processRequest = useMutation({
    mutationFn: async (data: any) => {
      const adminId = getAndSanitize("userId");
      return axios.post(
        `${API_BASE_URL}/api/panel/process-request`,
        { ...data, adminId, panelId: currentPanelId || "" },
        {
          headers: {
            ...headers,
            "X-Admin-ID": adminId,
          },
        }
      );
    },
  });

  // Obtener panelId activo al iniciar
  useEffect(() => {
    const token = getAndSanitize("token");
    if (!token) return;

    const userId = JSON.parse(atob(token.split(".")[1])).userId;
    
    // Usar useUserStore para obtener datos del usuario
    useUserStore.getState().fetchUser(userId).then(userData => {
      if (userData?.panels) {
        const activePanel = userData.panels.find((p: any) => p.isActive);
        if (activePanel) {
          updateCurrentPanelId(activePanel.id);
        } else {
          console.warn(
            "[UserPanel] No active panel found for user. Available panels:",
            userData.panels
          );
        }
      }
    }).catch(error => {
      console.error("[UserPanel] Error fetching panel ID:", error);
    });
  }, []);

  // Actualizar hasNewRequests basado en solicitudes pendientes
  useEffect(() => {
    // Usar el store de requests
    const pendingRequestsCount = requestsStore.pendingRequests.length;
    setHasNewRequests(pendingRequestsCount > 0);
  }, [requestsStore.pendingRequests]);

  // Socket event handling
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    const decodedToken = token ? JSON.parse(atob(token.split(".")[1])) : null;
    const currentAdminId = decodedToken?.userId;


    // Registrar el panel actual con su adminId
    if (currentAdminId && currentPanelId) {
      socket.emit("registerPanel", {
        adminId: currentAdminId,
        panelId: currentPanelId,
        timestamp: Date.now(),
      });
    }

    // Initial panel status request
    socket.emit("getPanelStatus");

    // Handle panel login status
    const handlePanelLoginStatus = (data: { status: boolean }) => {
      setPanelLoginStatus(data.status);
    };

    // Handle user created event - Filtrar por adminId
    const handleUserCreated = (newUser: any) => {

      // Solo procesar si es relevante para este panel/admin
      if (
        currentAdminId &&
        (newUser.adminId === currentAdminId || !newUser.adminId)
      ) {

        // Reproducir sonido solo si es para este panel
        if (soundEnabled) {
          playSound("userCreated");
        }

        queryClient.setQueryData(["panel-users"], (oldData: any[] = []) => {
          return [newUser, ...oldData];
        });
      } else {
      }
    };

    // Handle load request event - Filtrar por adminId
    const handleLoadRequest = (request: any) => {
      // Normalizar la solicitud recibida
      const normalizedRequest = normalizeRequest(request);
      
      if (!normalizedRequest) {
        console.error("[Panel] Invalid loadRequest data received:", request);
        return;
      }


      if (!currentAdminId) {
        return;
      }

      // Solo procesar si es relevante para este admin/panel
      if (!normalizedRequest.adminId || normalizedRequest.adminId === currentAdminId) {

        // Reproducir sonido solo para este panel
        playSoundForRequest(normalizedRequest);

        // Generar clave única para este panel específico
        const requestKey = `${normalizedRequest.id}|${normalizedRequest.status}|${Date.now()}`;
        processedRequestIds.current.add(requestKey);

        // Actualizar UI usando stores
        handleRequestStateUpdate(normalizedRequest);
      } else {
      }
    };

    // Handle withdraw request event - Filtrar por adminId
    const handleWithdrawRequest = (request: any) => {
      // Normalizar la solicitud recibida
      const normalizedRequest = normalizeRequest(request);
      
      if (!normalizedRequest) {
        console.error("[Panel] Invalid withdrawRequest data received:", request);
        return;
      }


      if (!currentAdminId) {
        return;
      }

      // Solo procesar si es relevante para este admin/panel
      if (!normalizedRequest.adminId || normalizedRequest.adminId === currentAdminId) {

        // Reproducir sonido solo para este panel
        playSoundForRequest(normalizedRequest);

        // Generar clave única para este panel específico
        const requestKey = `${normalizedRequest.id}|${normalizedRequest.status}|${Date.now()}`;
        processedRequestIds.current.add(requestKey);

        // Actualizar UI usando stores
        handleRequestStateUpdate(normalizedRequest);
      } else {
      }
    };

    const handlePermissonsChange = (response: any) => {
      const { updatedPermission, userId } = response;

      // Get stored userId and permissions
      const storedUserId = getAndSanitize("userId");

      // Only update if the userId matches
      if (storedUserId === userId) {
        try {
          // Get current permissions from localStorage
          const userPermissions = JSON.parse(
            getAndSanitize("user_permissions") || "{}"
          );

          // Create permission key in format "/endpoint:METHOD"
          const permKey = `${updatedPermission.endpoint}:${updatedPermission.method}`;

          // Update the specific permission
          userPermissions[permKey] = updatedPermission.allowed;

          // Save updated permissions back to localStorage
          sanitizeAndStore('user_permissions', JSON.stringify(userPermissions));
        } catch (error) {
          console.error("[Panel] Failed to update permissions:", error);
        }
      } else {
        console.warn("[Panel] Permission update rejected: userId mismatch");
      }
    };

    // Register event handlers
    socket.on("panelLoginStatus", handlePanelLoginStatus);
    socket.on("userCreated", handleUserCreated);
    socket.on("loadRequest", handleLoadRequest);
    socket.on("withdrawRequest", handleWithdrawRequest);
    socket.on("error", (error: Error) =>
      console.error("[Panel] Socket error:", error)
    );
    socket.on("permissionsChange", handlePermissonsChange);

    // Cleanup function
    return () => {
      socket.off("panelLoginStatus", handlePanelLoginStatus);
      socket.off("userCreated", handleUserCreated);
      socket.off("loadRequest", handleLoadRequest);
      socket.off("withdrawRequest", handleWithdrawRequest);
      socket.off("error");
      socket.off("permissionsChange");
    };
  }, [
    socket,
    isConnected,
    token,
    soundEnabled,
    lastReconnectTimestamp,
    queryClient,
    currentPanelId,
  ]);

  // Actualizar el panelId en el socket cuando cambie
  useEffect(() => {
    if (currentPanelId && socket && isConnected) {
      updatePanelId(currentPanelId);
    }
  }, [currentPanelId, socket, isConnected, updatePanelId]);

  const rejectRequest = useMutation({
    mutationFn: async (formData: FormData) => {
      const adminId = getAndSanitize("userId");
      formData.append("adminId", adminId || "");
      if (currentPanelId) {
        formData.append("panelId", currentPanelId);
      }
      return axios.post(
        `${API_BASE_URL}/api/panel/load-requests/reject`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
            "X-Admin-ID": adminId,
          },
        }
      );
    },
  });

  const toggleSound = () => {
    setSoundEnabled((prev) => {
      const newValue = !prev;
      sanitizeAndStore('soundEnabled', String(newValue));
      return newValue;
    });
  };

  const createUser = useMutation({
    mutationFn: async (data: any) => {
      const adminId = getAndSanitize("userId");
      const response = await axios.post(
        `${API_BASE_URL}/api/panel/users`,
        { ...data, adminId, panelId: currentPanelId || "" },
        {
          headers: {
            ...headers,
            "X-Admin-ID": adminId,
          },
        }
      );
      if (!response.data.data.username) {
        throw new Error("Invalid server response: missing username");
      }
      return response.data.data;
    },
    onMutate: () => {
      const previousUsers = queryClient.getQueryData(["panel-users"]);
      return { previousUsers };
    },
    onSuccess: (newUser) => {
      queryClient.setQueryData(["panel-users"], (oldData: any[] = []) => [
        newUser,
        ...oldData.filter((user) => user.id !== newUser.id),
      ]);
    },
    onError: (err, context: any) => {
      console.error("[CreateUser] Error:", err);
      queryClient.setQueryData(["panel-users"], context.previousUsers);
    },
  });

  const deposit = useMutation({
    mutationFn: async (formData: FormData) => {
      const adminId = getAndSanitize("userId");
      formData.append("adminId", adminId || "");
      if (currentPanelId) {
        formData.append("panelId", currentPanelId);
      }
      return axios.post(
        `${API_BASE_URL}/api/panel/operations/deposit`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
            "X-Admin-ID": adminId,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panel-users"] });
    },
  });

  const withdraw = useMutation({
    mutationFn: async (formData: FormData) => {
      const adminId = getAndSanitize("userId");
      formData.append("adminId", adminId || "");
      if (currentPanelId) {
        formData.append("panelId", currentPanelId);
      }
      return axios.post(
        `${API_BASE_URL}/api/panel/operations/withdraw`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
            "X-Admin-ID": adminId,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panel-users"] });
    },
    onError: (error) => {
      console.error("Withdraw error:", error);
    },
  });

  const checkBalance = useMutation({
    mutationFn: async (data: { username: string; panelId?: string }) => {
      const adminId = getAndSanitize("userId");

      try {
        const panelId = data.panelId || currentPanelId;
        if (!panelId) {
          console.error("[Balance] No panel ID available");
          throw new Error("Se requiere seleccionar un panel");
        }


        const response = await axios.post<{ balance: number }>(
          `${API_BASE_URL}/api/panel/operations/balance`,
          {
            username: data.username,
            adminId,
            panelId,
          },
          {
            headers: {
              ...headers,
              "X-Admin-ID": adminId,
            },
          }
        );

        return response.data;
      } catch (error: any) {
        console.error("[Balance] Request failed:", error);
        throw error;
      }
    },
    onSuccess: (response, variables) => {

      queryClient.invalidateQueries({ queryKey: ["panel-users"] });

      queryClient.setQueryData(["panel-users"], (oldData: any[] = []) => {
        if (!oldData) return [];

        return oldData.map((user) => {
          if (user.username === variables.username) {
            return { ...user, currentBalance: response.balance };
          }
          return user;
        });
      });
    },
  });

  // Función para obtener configuraciones
  const getSettings = async () => {
    const token = getAndSanitize("token");
    const adminId = getAndSanitize("userId");
    if (!token) throw new Error("No token found");
    if (!currentPanelId) {
      console.warn("[Settings] No currentPanelId available");
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Admin-ID": adminId,
        },
        params: {
          adminId,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("[Settings] Fetch error:", error);
      throw error;
    }
  };

  // Función para actualizar configuraciones
  const updateSettings = async (values: any) => {
    const token = getAndSanitize("token");
    const adminId = getAndSanitize("userId");
    if (!token) throw new Error("No token found");
    if (!currentPanelId) {
      console.warn("[Settings] No currentPanelId available for updateSettings");
    }

    const response = await axios.put(
      `${API_BASE_URL}/api/settings`,
      { ...values, adminId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Admin-ID": adminId,
        },
      }
    );
    queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
    return response.data;
  };

  // Función para actualizar el panelId
  const updateCurrentPanelId = useCallback((panelId: string | null) => {
    setCurrentPanelId(panelId);
    if (panelId) {
      sanitizeAndStore('panelId', panelId);
    } else {
      localStorage.removeItem("panelId");
    }
  }, []);

  // Cargar el panelId guardado al iniciar
  useEffect(() => {
    const savedPanelId = getAndSanitize("panelId");
    if (savedPanelId) {
      
      // Verificar que el panel exista usando el store
      panelsStore.fetchPanels().then(panels => {
        const panelExists = panels.some(panel => panel.id === savedPanelId);
        if (panelExists) {
          setCurrentPanelId(savedPanelId);
        } else {
          console.warn("[UserPanelContext] Panel ID en localStorage no es válido:", savedPanelId);
          localStorage.removeItem("panelId");
        }
      }).catch(error => {
        console.error("[UserPanel] Error validating panel:", error);
      });
    }
  }, []);

  // Ejecutar operaciones con selección de panel
  const executeWithPanel = async <T,>(
    operation: string,
    data: any,
    callback: (panelId: string, data: any) => Promise<T>
  ): Promise<T> => {
    try {
      // Obtener todos los paneles disponibles
      const panels = await panelsStore.fetchPanels();

      // Si no hay paneles, error
      if (!panels || panels.length === 0) {
        throw new Error("No hay paneles disponibles");
      }

      // Usar el primer panel disponible directamente
      const firstPanel = panels[0];
      const panelId = firstPanel.id;

      // Actualizar localStorage y estado
      sanitizeAndStore('panelId', panelId);
      setCurrentPanelId(panelId);

      // Ejecutar con el panel seleccionado
      return await callback(panelId, data);
    } catch (error) {
      console.error(`[Panel] Error al ejecutar ${operation}:`, error);
      throw error;
    }
  };

  // Proporcionar los valores reducidos al contexto
  const value = {
    currentPanelId,
    soundEnabled,
    toggleSound,
    createUser: createUser.mutate,
    deposit: deposit.mutateAsync,
    withdraw: withdraw.mutateAsync,
    isDepositing: deposit.isPending,
    isWithdrawing: withdraw.isPending,
    checkBalance: checkBalance.mutateAsync,
    processRequest: processRequest.mutateAsync,
    pendingRequests,
    processedRequests,
    setPendingRequests,
    setProcessedRequests,
    hasNewRequests,
    setHasNewRequests,
    rejectRequest: rejectRequest.mutateAsync,
    getSettings,
    updateSettings,
    panelLoginStatus,
    health: {
      ping: health.ping,
      status: health.status,
      memory: {
        heapTotal: 0,
        heapUsed: 0,
        external: 0,
        arrayBuffers: 0,
        rss: 0,
      },
      cpu: {
        user: 0,
        system: 0,
      },
      timestamp: Date.now(),
    },
    checkHistory: checkHistory.mutateAsync,
    updateCurrentPanelId,
    executeWithPanel,
  };

  return (
    <UserPanelContext.Provider value={value}>
      {children}
    </UserPanelContext.Provider>
  );
};

export const useUserPanel = () => {
  const context = useContext(UserPanelContext);
  if (context === undefined) {
    throw new Error("useUserPanel must be used within a UserPanelProvider");
  }
  return context;
};